import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, doc, deleteDoc, updateDoc,
  query, orderBy, where, getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { deleteImageFromBunny } from '../../utils/bunnyUpload';
import {
  Search, Trash2, Loader2, Plus,
  BookOpen, Edit2, Eye, EyeOff, GraduationCap, DollarSign, Users, ChevronDown, Play, Globe, Heart
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
const LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };
const STATUSES = { draft: 'مسودة', published: 'منشورة' };


export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  const [isFilterLevelOpen, setIsFilterLevelOpen] = useState(false);
  const [isFilterStatusOpen, setIsFilterStatusOpen] = useState(false);
  const [isFilterCategoryOpen, setIsFilterCategoryOpen] = useState(false);

  const [isDeleting, setIsDeleting] = useState(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  // ─── Fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({
        id: d.id, ...d.data(),
        dateStr: d.data().createdAt
          ? d.data().createdAt.toDate().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
          : '–',
      }));
      setCourses(data);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Update dynamic categories list for FILTER (only those with courses)
  const [categoriesWithCounts, setCategoriesWithCounts] = useState([]);

  useEffect(() => {
    // Calculate counts and unique categories for FILTER - only show active ones
    const counts = {};
    courses.forEach(c => {
      if (c.category) counts[c.category] = (counts[c.category] || 0) + 1;
    });
    const filterList = Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    setCategoriesWithCounts(filterList);
  }, [courses]);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'instructor'), where('instructorData.status', '==', 'approved'));
        const snap = await getDocs(q);
        setInstructors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching instructors:", err);
      }
    };
    fetchInstructors();
  }, []);

  // ─── Filter / Search ──────────────────────────────────────────────
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    setFiltered(courses.filter(c => {
      const matchSearch = !term || c.title?.toLowerCase().includes(term) || c.shortDesc?.toLowerCase().includes(term);
      const matchLevel = filterLevel === 'all' || c.level === filterLevel;
      const matchStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchCategory = filterCategory === 'all' || c.category === filterCategory;
      return matchSearch && matchLevel && matchStatus && matchCategory;
    }));
  }, [searchTerm, filterLevel, filterStatus, filterCategory, courses]);

  // ─── UI ───────────────────────────────────────────────────────────
  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">إدارة الدورات</h1>
          <p className="text-slate-500 dark:text-zinc-400">إنشاء وتعديل وإدارة الدورات التعليمية.</p>
        </div>
        <button onClick={() => navigate('/admin/courses/new')}
          className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-primary-500/25">
          <Plus className="w-5 h-5" /><span>إنشاء دورة جديدة</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl shadow-sm mb-6">
        <div className="p-4 md:p-6 flex flex-col sm:flex-row gap-4 items-center justify-between bg-slate-50/50 dark:bg-zinc-900/50 rounded-t-3xl">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            <input type="text" placeholder="ابحث بعنوان الدورة..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-11 py-2.5 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white transition-all" />
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Level Filter */}
            <div className="relative">
              <button
                onClick={() => { setIsFilterLevelOpen(!isFilterLevelOpen); setIsFilterStatusOpen(false); setIsFilterCategoryOpen(false); }}
                className="px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl flex items-center gap-2 text-slate-900 dark:text-white font-bold transition-all hover:border-primary-500 shadow-sm"
              >
                <span>{filterLevel === 'all' ? 'المستوى: الكل' : LEVELS[filterLevel]}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isFilterLevelOpen ? 'rotate-180 text-primary-500' : ''}`} />
              </button>
              <AnimatePresence>
                {isFilterLevelOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 mt-2 p-1.5 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl z-[80] min-w-[160px]"
                  >
                    <button onClick={() => { setFilterLevel('all'); setIsFilterLevelOpen(false); }} className={`w-full text-right px-3 py-2 rounded-lg font-bold transition-all ${filterLevel === 'all' ? 'bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-zinc-400'}`}>الكل</button>
                    {Object.entries(LEVELS).map(([v, l]) => (
                      <button key={v} onClick={() => { setFilterLevel(v); setIsFilterLevelOpen(false); }} className={`w-full text-right px-3 py-2 rounded-lg font-bold transition-all mt-1 ${filterLevel === v ? 'bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-zinc-400'}`}>{l}</button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => { setIsFilterStatusOpen(!isFilterStatusOpen); setIsFilterLevelOpen(false); setIsFilterCategoryOpen(false); }}
                className="px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl flex items-center gap-2 text-slate-900 dark:text-white font-bold transition-all hover:border-primary-500 shadow-sm"
              >
                <span>{filterStatus === 'all' ? 'الحالة: الكل' : STATUSES[filterStatus]}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isFilterStatusOpen ? 'rotate-180 text-primary-500' : ''}`} />
              </button>
              <AnimatePresence>
                {isFilterStatusOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 mt-2 p-1.5 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl z-[80] min-w-[160px]"
                  >
                    <button onClick={() => { setFilterStatus('all'); setIsFilterStatusOpen(false); }} className={`w-full text-right px-3 py-2 rounded-lg font-bold transition-all ${filterStatus === 'all' ? 'bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-zinc-400'}`}>الكل</button>
                    {Object.entries(STATUSES).map(([v, l]) => (
                      <button key={v} onClick={() => { setFilterStatus(v); setIsFilterStatusOpen(false); }} className={`w-full text-right px-3 py-2 rounded-lg font-bold transition-all mt-1 ${filterStatus === v ? 'bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-zinc-400'}`}>{l}</button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Category Filter */}
            <div className="relative">
              <button
                onClick={() => { setIsFilterCategoryOpen(!isFilterCategoryOpen); setIsFilterLevelOpen(false); setIsFilterStatusOpen(false); }}
                className="px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl flex items-center gap-2 text-slate-900 dark:text-white font-bold transition-all hover:border-primary-500 shadow-sm"
              >
                <span>{filterCategory === 'all' ? `الفئة: الكل (${courses.length})` : filterCategory}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isFilterCategoryOpen ? 'rotate-180 text-primary-500' : ''}`} />
              </button>
              <AnimatePresence>
                {isFilterCategoryOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 mt-2 p-1.5 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl z-[80] min-w-[200px] max-h-[300px] overflow-y-auto custom-scrollbar"
                  >
                    <button onClick={() => { setFilterCategory('all'); setIsFilterCategoryOpen(false); }} className={`w-full text-right px-3 py-2 rounded-lg font-bold transition-all flex items-center justify-between ${filterCategory === 'all' ? 'bg-primary-50 text-primary-600 dark:bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-zinc-400'}`}>
                      <span>الكل</span>
                      <span className="text-[10px] opacity-60">({courses.length})</span>
                    </button>
                    {categoriesWithCounts.map(cat => (
                      <button key={cat.name} onClick={() => { setFilterCategory(cat.name); setIsFilterCategoryOpen(false); }} className={`w-full text-right px-3 py-2 rounded-lg font-bold transition-all mt-1 flex items-center justify-between ${filterCategory === cat.name ? 'bg-primary-50 text-primary-600 dark:bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-zinc-400'}`}>
                        <span className="truncate">{cat.name}</span>
                        <span className="text-[10px] opacity-60 shrink-0">({cat.count})</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Outside Click Guards */}
            {(isFilterLevelOpen || isFilterStatusOpen || isFilterCategoryOpen) && (
              <div className="fixed inset-0 z-[70]" onClick={() => { setIsFilterLevelOpen(false); setIsFilterStatusOpen(false); setIsFilterCategoryOpen(false); }} />
            )}
          </div>
          {(filterLevel !== 'all' || filterStatus !== 'all' || filterCategory !== 'all' || searchTerm) && (
            <button onClick={() => { setFilterLevel('all'); setFilterStatus('all'); setFilterCategory('all'); setSearchTerm(''); }}
              className="text-mdfont-bold text-slate-400 hover:text-red-500 transition-colors">مسح الفلاتر</button>
          )}
        </div>
      </div>

      {
        loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
            <p className="font-bold">جاري تحميل الدورات...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-slate-500 bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-white/10">
            <GraduationCap className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
            <p className="text-xl font-bold mb-2">لا توجد دورات</p>
            <p className="text-md font-medium text-slate-500">ابدأ بإنشاء دورتك الأولى!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map(course => (
              <div key={course.id} onClick={() => navigate(`/admin/courses/${course.id}`)} className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all group flex flex-col cursor-pointer">
                <div className="aspect-[1350/1080] bg-gradient-to-br from-primary-500 to-red-700 relative overflow-hidden">
                  {course.thumbnail ? (<img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />) : (<div className="w-full h-full flex items-center justify-center"><BookOpen className="w-16 h-16 text-white/30" /></div>)}
                  <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-extrabold border ${course.status === 'published' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-slate-800/80 text-slate-200 border-slate-600'}`}>
                    {STATUSES[course.status] || course.status}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-extrabold text-slate-900 dark:text-white text-lg leading-snug line-clamp-2">{course.title}</h3>
                    <span className={`shrink-0 px-2.5 py-1 rounded-lg text-md font-bold ${course.level === 'beginner' ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400' : course.level === 'intermediate' ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'}`}>
                      {LEVELS[course.level] || course.level}
                    </span>
                  </div>
                  {course.category && (
                    <div className="flex items-center gap-1.5 text-primary-600 dark:text-primary-400 font-bold mb-2 text-md">
                      <Globe className="w-3.5 h-3.5" />
                      <span>{course.category}</span>
                    </div>
                  )}
                  <p className="text-md text-slate-500 dark:text-zinc-400 line-clamp-2 mb-4 flex-1">{course.shortDesc || course.description || 'لا يوجد وصف.'}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-md text-slate-500 dark:text-zinc-400 mb-4 border-t border-slate-100 dark:border-white/5 pt-4">
                    <span className="flex items-center gap-1.5 font-bold"><DollarSign className="w-4 h-4 text-emerald-500 shrink-0" />{course.price > 0 ? `$${course.price}` : 'مجاني'}</span>
                    <span className="flex items-center gap-1.5 font-bold"><Users className="w-4 h-4 text-primary-500 shrink-0" />{course.studentsCount || 0} طالب</span>
                    <span className="flex items-center gap-1.5 font-bold"><Play className="w-4 h-4 text-slate-400 shrink-0" />{course.totalLessons || 0} درس</span>
                    <span className="flex items-center gap-1.5 font-bold" title="عدد الإعجابات"><Heart className="w-4 h-4 text-rose-500 shrink-0" />{course.favorites?.length || 0}</span>
                    <span className="mr-auto text-sm shrink-0 whitespace-nowrap">{course.dateStr}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/courses/edit/${course.id}`); }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-white rounded-xl font-bold text-md transition-all">
                      <Edit2 className="w-4 h-4" /> تعديل
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleToggleStatus(course); }}
                      className={`px-3.5 py-2.5 rounded-xl font-bold text-md transition-all ${course.status === 'published' ? 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400'}`}
                      title={course.status === 'published' ? 'إلغاء النشر' : 'نشر الدورة'}>
                      {course.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(course.id); }} disabled={isDeleting === course.id}
                      className="px-3.5 py-2.5 bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 rounded-xl transition-all disabled:opacity-50" title="حذف الدورة">
                      {isDeleting === course.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  )
}

